// Runtime schemas for every server response the SDK consumes. These are the
// single source of truth for response types (inferred below) AND the runtime
// validation boundary: the SDK never trusts a server payload without parsing it
// here first. The shapes are MANUALLY SYNCHRONIZED with the game-gateway's
// public JSON (services/game-gateway/app/httpapi/httpapi.go). When the gateway
// response shape changes, update these schemas in the same change.
import { z } from 'zod';
/** Round lifecycle states, mirrored from the RoundState proto enum. */
export const RoundStateSchema = z.enum([
    'ROUND_STATE_UNSPECIFIED',
    'ROUND_STATE_CREATED',
    'ROUND_STATE_RESULT_REQUESTED',
    'ROUND_STATE_RESULT_READY',
    'ROUND_STATE_DEBIT_REQUESTED',
    'ROUND_STATE_DEBIT_CONFIRMED',
    'ROUND_STATE_RESULT_RELEASED',
    'ROUND_STATE_CREDIT_REQUESTED',
    'ROUND_STATE_SETTLED',
    'ROUND_STATE_ROLLBACK_REQUESTED',
    'ROUND_STATE_ROLLED_BACK',
    'ROUND_STATE_FAILED',
    'ROUND_STATE_MANUAL_REVIEW',
]);
export const WinLineSchema = z.object({
    line_index: z.number().int(),
    symbol: z.number().int(),
    count: z.number().int(),
    win_minor: z.number().int(),
});
export const SpinStepSchema = z.object({
    step_type: z.string(),
    reel_stops: z.array(z.number().int()),
    grid: z.array(z.number().int()),
    win_lines: z.array(WinLineSchema),
    scatter_count: z.number().int(),
    step_win_minor: z.number().int(),
    free_spins_awarded: z.number().int(),
});
/** Coerces null/undefined repeated fields to [] for legacy server payloads. */
function nonNullArray(item) {
    return z.preprocess((val) => (val == null ? [] : val), z.array(item));
}
const LightningSlotSchema = z.object({
    number: z.number().int().min(1).max(6),
    tier_id: z.string(),
    tenths: z.number().int(),
});
const LightningNumberDiceBlockSchema = z.object({
    selected_number: z.number().int().min(1).max(6),
    dice: nonNullArray(z.number().int().min(1).max(6)),
    match_count: z.number().int().min(0).max(3),
    lightning_number_count: z.number().int().min(0).max(3),
    lightning_numbers: nonNullArray(z.number().int().min(1).max(6)),
    lightning_slots: nonNullArray(LightningSlotSchema),
    lightning_multipliers: nonNullArray(z.number().int()).optional(),
    selected_is_lightning: z.boolean(),
    applied_lightning_multiplier_tenths: z.number().int(),
    base_profit_multiplier_tenths: z.number().int(),
    lightning_profit_multiplier_tenths: z.number().int(),
    profit_multiplier_tenths: z.number().int(),
    stake_return_multiplier_tenths: z.number().int(),
    total_payout_multiplier_tenths: z.number().int(),
    bet_minor: z.number().int(),
    profit_amount_minor: z.number().int(),
    stake_return_amount_minor: z.number().int(),
    total_payout_amount_minor: z.number().int(),
});
export const SpinResultSchema = z.object({
    round_id: z.string(),
    state: RoundStateSchema,
    processing: z.boolean().optional().default(false),
    total_bet_minor: z.number().int().optional().default(0),
    total_win_minor: z.number().int().optional().default(0),
    currency: z.string().optional().default(''),
    balance_after_minor: z.number().int().optional(),
    balance_version: z.number().int().optional(),
    result_id: z.string().optional(),
    result_hash: z.string().optional(),
    math_version: z.string().optional(),
    max_win_capped: z.boolean().optional(),
    steps: z.array(SpinStepSchema).optional().default([]),
    // Dice-game authoritative fields. Present only for dice rounds; absent for
    // slot games. The client renders these EXACT values and never derives the
    // outcome, multiplier, or payout locally.
    dice: nonNullArray(z.number().int().min(1).max(6)).optional(),
    selected_number: z.number().int().min(1).max(6).optional(),
    match_count: z.number().int().min(0).max(3).optional(),
    multiplier: z.number().int().optional(),
    payout_minor: z.number().int().optional(),
    base_multiplier_tenths: z.number().int().optional(),
    lightning_triggered: z.boolean().optional(),
    lightning_tier: z.string().optional(),
    lightning_multiplier_tenths: z.number().int().optional(),
    total_multiplier_tenths: z.number().int().optional(),
    lightning_dice: z
        .object({
        selected_number: z.number().int().min(1).max(6),
        dice: z.array(z.number().int().min(1).max(6)),
        match_count: z.number().int().min(0).max(3),
        base_multiplier_tenths: z.number().int(),
        lightning_triggered: z.boolean(),
        lightning_tier: z.string().optional(),
        lightning_multiplier_tenths: z.number().int(),
        total_multiplier_tenths: z.number().int(),
        bet_minor: z.number().int(),
        payout_minor: z.number().int(),
    })
        .optional(),
    lightning_number_count: z.number().int().min(0).max(3).optional(),
    lightning_numbers: nonNullArray(z.number().int().min(1).max(6)).optional(),
    lightning_slots: nonNullArray(LightningSlotSchema).optional(),
    lightning_multipliers: nonNullArray(z.number().int()).optional(),
    selected_is_lightning: z.boolean().optional(),
    applied_lightning_multiplier_tenths: z.number().int().optional(),
    base_profit_multiplier_tenths: z.number().int().optional(),
    lightning_profit_multiplier_tenths: z.number().int().optional(),
    profit_multiplier_tenths: z.number().int().optional(),
    stake_return_multiplier_tenths: z.number().int().optional(),
    total_payout_multiplier_tenths: z.number().int().optional(),
    profit_amount_minor: z.number().int().optional(),
    stake_return_amount_minor: z.number().int().optional(),
    total_payout_amount_minor: z.number().int().optional(),
    lightning_number_dice: LightningNumberDiceBlockSchema.optional(),
    seahorse_race: z
        .object({
        selected_lane: z.number().int().min(1).max(3),
        target_multiplier_hundredths: z.number().int().min(101).max(1000),
        lane_multipliers_hundredths: nonNullArray(z.number().int()),
        selected_lane_multiplier_hundredths: z.number().int(),
        won: z.boolean(),
        bet_minor: z.number().int(),
        payout_minor: z.number().int(),
    })
        .optional(),
    // Shareable public replay metadata, present once the round has settled. The
    // id maps to GET /public/replays/{id}; it is safe to expose to this player.
    replay_id: z.string().optional(),
    replay_version: z.number().int().optional(),
    replay_public: z.boolean().optional(),
});
/**
 * Public replay projection returned by GET /public/replays/{id}. It carries
 * ONLY the outcome + public round metadata: no player, operator, or session
 * identity. Used by the standalone replay page (no game session required).
 */
export const PublicReplaySchema = z.object({
    replay_id: z.string(),
    game_id: z.string(),
    selected_number: z.number().int().min(1).max(6).optional().default(0),
    dice: z.array(z.number().int().min(1).max(6)).optional().default([]),
    match_count: z.number().int().min(0).max(3).optional().default(0),
    multiplier: z.number().int().optional().default(0),
    bet_minor: z.number().int().optional().default(0),
    win_minor: z.number().int().optional().default(0),
    currency: z.string().optional().default(''),
    created_at_unix: z.number().int().optional().default(0),
    result_hash: z.string().optional().default(''),
    replay_version: z.number().int().optional().default(1),
    base_multiplier_tenths: z.number().int().optional(),
    lightning_triggered: z.boolean().optional(),
    lightning_tier: z.string().optional(),
    lightning_multiplier_tenths: z.number().int().optional(),
    total_multiplier_tenths: z.number().int().optional(),
    lightning_number_count: z.number().int().optional(),
    lightning_numbers: nonNullArray(z.number().int().min(1).max(6)).optional(),
    lightning_slots: nonNullArray(LightningSlotSchema).optional(),
    lightning_multipliers: nonNullArray(z.number().int()).optional(),
    selected_is_lightning: z.boolean().optional(),
    applied_lightning_multiplier_tenths: z.number().int().optional(),
    base_profit_multiplier_tenths: z.number().int().optional(),
    lightning_profit_multiplier_tenths: z.number().int().optional(),
    profit_multiplier_tenths: z.number().int().optional(),
    stake_return_multiplier_tenths: z.number().int().optional(),
    total_payout_multiplier_tenths: z.number().int().optional(),
    profit_amount_minor: z.number().int().optional(),
    stake_return_amount_minor: z.number().int().optional(),
    target_multiplier_hundredths: z.number().int().optional(),
    lane_multipliers_hundredths: nonNullArray(z.number().int()).optional(),
    selected_lane_multiplier_hundredths: z.number().int().optional(),
    seahorse_won: z.boolean().optional(),
});
export const ExchangeResultSchema = z.object({
    access_token: z.string().min(1),
    session_id: z.string(),
    game_id: z.string(),
    currency: z.string(),
    mode: z.string(),
    expires_at_unix: z.number().int(),
    // Server-authoritative bet rules (minor units). The client may ONLY offer
    // these denominations; the server rejects any other bet. Optional for
    // backward compatibility — the client keeps a built-in fallback if absent.
    bet_denominations: z.array(z.number().int().positive()).optional().default([]),
    min_bet_minor: z.number().int().optional(),
    max_bet_minor: z.number().int().optional(),
    max_payout_minor: z.number().int().optional(),
    min_target_multiplier_hundredths: z.number().int().optional(),
    max_target_multiplier_hundredths: z.number().int().optional(),
    math_profile_id: z.string().optional(),
    math_version: z.string().optional(),
    target_rtp_bps: z.number().int().optional(),
    config_version: z.number().int().optional(),
});
export const RecoveryResultSchema = z.object({
    has_unresolved: z.boolean(),
    round: SpinResultSchema.optional(),
});
/**
 * Server-authoritative balance, returned by GET /v1/balance. The client
 * DISPLAYS this value only; it never computes balance locally and never calls
 * the operator wallet directly (the gateway brokers it via the wallet
 * orchestrator).
 */
export const BalanceResultSchema = z.object({
    balance_minor: z.number().int(),
    currency: z.string(),
    balance_version: z.number().int(),
});
export const HeartbeatResultSchema = z.object({
    state: z.string(),
    expires_at_unix: z.number().int().optional(),
});
export const CloseResultSchema = z.object({
    state: z.string(),
});
export const ApiErrorBodySchema = z.object({
    code: z.string(),
    message: z.string(),
    correlation_id: z.string().optional(),
});
//# sourceMappingURL=schema.js.map